import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { getSPIs, createSPI, addSPIData, getSPIData } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { 
  Plus, BarChart3, TrendingUp, TrendingDown, Minus, Target,
  AlertTriangle, CheckCircle, Activity, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const SPI_CATEGORIES = [
  { value: 'leading', label: 'Leading Indicator', label_ar: 'مؤشر رائد', description: 'Proactive measures' },
  { value: 'lagging', label: 'Lagging Indicator', label_ar: 'مؤشر متأخر', description: 'Reactive measures' },
];

const TREND_INFO = {
  improving: { icon: TrendingDown, color: 'text-emerald-400', bg: 'bg-emerald-600/20', label: 'Improving' },
  stable: { icon: Minus, color: 'text-slate-400', bg: 'bg-slate-600/20', label: 'Stable' },
  declining: { icon: TrendingUp, color: 'text-red-400', bg: 'bg-red-600/20', label: 'Declining' },
};

const SPICard = ({ spi, onAddData, onViewDetails, language }) => {
  const trendInfo = TREND_INFO[spi.trend] || TREND_INFO.stable;
  const TrendIcon = trendInfo.icon;
  const isAlert = spi.current_value >= spi.alert_threshold;
  const progressValue = Math.min((spi.current_value / spi.target_value) * 100, 100);
  const isAboveTarget = spi.current_value > spi.target_value;

  return (
    <Card 
      className={cn(
        "bg-slate-900/60 border-slate-800 hover:border-slate-700 transition-all cursor-pointer",
        isAlert && "border-l-4 border-l-red-500"
      )}
      onClick={() => onViewDetails(spi)}
      data-testid={`spi-card-${spi.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm text-sky-400">{spi.spi_number}</span>
            <Badge variant="outline" className="border-slate-700 text-slate-400 text-xs">
              {SPI_CATEGORIES.find(c => c.value === spi.category)?.label || spi.category}
            </Badge>
          </div>
          <div className={cn("flex items-center gap-1 px-2 py-1 rounded-full text-xs", trendInfo.bg)}>
            <TrendIcon className={cn("w-3 h-3", trendInfo.color)} />
            <span className={trendInfo.color}>{trendInfo.label}</span>
          </div>
        </div>

        <h3 className="text-lg font-semibold text-white mb-1">
          {language === 'ar' && spi.name_ar ? spi.name_ar : spi.name}
        </h3>
        <p className="text-sm text-slate-400 line-clamp-2 mb-4">
          {language === 'ar' && spi.description_ar ? spi.description_ar : spi.description}
        </p>

        <div className="space-y-3">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-sm text-slate-500">Current Value</p>
              <p className={cn(
                "text-3xl font-bold",
                isAlert ? "text-red-400" : isAboveTarget ? "text-emerald-400" : "text-white"
              )}>
                {spi.current_value.toFixed(1)}
                <span className="text-sm text-slate-500 ml-1">{spi.unit}</span>
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-500">Target</p>
              <p className="text-lg text-slate-300">{spi.target_value} {spi.unit}</p>
            </div>
          </div>

          <div className="space-y-1">
            <div className="flex justify-between text-xs text-slate-500">
              <span>Progress to Target</span>
              <span>{progressValue.toFixed(0)}%</span>
            </div>
            <Progress 
              value={progressValue} 
              className="h-2 bg-slate-800"
            />
          </div>

          {isAlert && (
            <div className="flex items-center gap-2 p-2 bg-red-950/30 rounded-lg text-sm text-red-400">
              <AlertTriangle className="w-4 h-4" />
              Above alert threshold ({spi.alert_threshold} {spi.unit})
            </div>
          )}
        </div>

        <div className="flex gap-2 mt-4 pt-3 border-t border-slate-800">
          <Button 
            size="sm" 
            variant="outline" 
            className="flex-1 border-slate-700 text-slate-300"
            onClick={(e) => { e.stopPropagation(); onAddData(spi); }}
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Data
          </Button>
          <Button 
            size="sm" 
            className="flex-1 bg-sky-600 hover:bg-sky-700"
            onClick={(e) => { e.stopPropagation(); onViewDetails(spi); }}
          >
            <Activity className="w-3 h-3 mr-1" />
            View Trend
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default function SPIsPage() {
  const { t } = useTranslation();
  const { api, user } = useAuth();
  const { language, isRTL } = useLanguage();
  const [spis, setSPIs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAddDataDialog, setShowAddDataDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedSPI, setSelectedSPI] = useState(null);
  const [spiHistory, setSPIHistory] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');

  const [formData, setFormData] = useState({
    name: '',
    name_ar: '',
    description: '',
    description_ar: '',
    category: 'leading',
    unit: '%',
    target_value: 0,
    alert_threshold: 0,
    calculation_method: ''
  });

  const [dataForm, setDataForm] = useState({
    value: 0,
    period: new Date().toISOString().slice(0, 7), // YYYY-MM format
    notes: ''
  });

  useEffect(() => {
    fetchSPIs();
  }, [filterCategory]);

  const fetchSPIs = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCategory !== 'all') params.category = filterCategory;
      const response = await getSPIs(api, params);
      setSPIs(response.data || []);
    } catch (error) {
      console.error('Failed to fetch SPIs:', error);
      toast.error('Failed to load SPIs');
    } finally {
      setLoading(false);
    }
  };

  const fetchSPIHistory = async (spiId) => {
    try {
      const response = await getSPIData(api, spiId, { limit: 12 });
      setSPIHistory(response.data || []);
    } catch (error) {
      console.error('Failed to fetch SPI history:', error);
      setSPIHistory([]);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createSPI(api, formData);
      toast.success('SPI created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchSPIs();
    } catch (error) {
      console.error('Failed to create SPI:', error);
      toast.error('Failed to create SPI');
    }
  };

  const handleAddData = async (e) => {
    e.preventDefault();
    if (!selectedSPI) return;
    try {
      await addSPIData(api, selectedSPI.id, dataForm);
      toast.success('Data point added successfully');
      setShowAddDataDialog(false);
      setDataForm({ value: 0, period: new Date().toISOString().slice(0, 7), notes: '' });
      fetchSPIs();
      if (showDetailDialog) {
        fetchSPIHistory(selectedSPI.id);
      }
    } catch (error) {
      console.error('Failed to add data point:', error);
      toast.error('Failed to add data point');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', name_ar: '', description: '', description_ar: '',
      category: 'leading', unit: '%', target_value: 0, alert_threshold: 0, calculation_method: ''
    });
  };

  const openAddDataDialog = (spi) => {
    setSelectedSPI(spi);
    setShowAddDataDialog(true);
  };

  const openDetailDialog = async (spi) => {
    setSelectedSPI(spi);
    await fetchSPIHistory(spi.id);
    setShowDetailDialog(true);
  };

  // Stats calculations
  const totalSPIs = spis.length;
  const leadingCount = spis.filter(s => s.category === 'leading').length;
  const laggingCount = spis.filter(s => s.category === 'lagging').length;
  const alertCount = spis.filter(s => s.current_value >= s.alert_threshold).length;
  const improvingCount = spis.filter(s => s.trend === 'improving').length;

  const canCreate = ['admin', 'safety_manager'].includes(user?.role);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white font-barlow tracking-tight flex items-center gap-3" data-testid="spis-title">
            <BarChart3 className="w-8 h-8 text-sky-400" />
            Safety Performance Indicators
          </h1>
          <p className="text-slate-400 mt-1">Monitor and track key safety metrics</p>
        </div>
        {canCreate && (
          <Button onClick={() => setShowCreateDialog(true)} className="bg-sky-600 hover:bg-sky-700" data-testid="new-spi-btn">
            <Plus className="w-4 h-4 mr-2" />New SPI
          </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Total SPIs</p>
                <p className="text-2xl font-bold text-white">{totalSPIs}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-sky-600/20 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-sky-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Leading</p>
                <p className="text-2xl font-bold text-emerald-400">{leadingCount}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-600/20 flex items-center justify-center">
                <ArrowUpRight className="w-5 h-5 text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Lagging</p>
                <p className="text-2xl font-bold text-blue-400">{laggingCount}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center">
                <ArrowDownRight className="w-5 h-5 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Alerts</p>
                <p className={cn("text-2xl font-bold", alertCount > 0 ? "text-red-400" : "text-emerald-400")}>{alertCount}</p>
              </div>
              <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center", alertCount > 0 ? "bg-red-600/20" : "bg-emerald-600/20")}>
                <AlertTriangle className={cn("w-5 h-5", alertCount > 0 ? "text-red-400" : "text-emerald-400")} />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Improving</p>
                <p className="text-2xl font-bold text-violet-400">{improvingCount}</p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-violet-600/20 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-violet-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-slate-900/60 border-slate-800">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm">Filter by category:</span>
            <div className="flex gap-2">
              <Button
                variant={filterCategory === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilterCategory('all')}
                className={filterCategory === 'all' ? 'bg-sky-600' : 'border-slate-700 text-slate-300'}
              >
                All
              </Button>
              {SPI_CATEGORIES.map(c => (
                <Button
                  key={c.value}
                  variant={filterCategory === c.value ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterCategory(c.value)}
                  className={filterCategory === c.value ? 'bg-sky-600' : 'border-slate-700 text-slate-300'}
                >
                  {language === 'ar' ? c.label_ar : c.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* SPI Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="spinner w-8 h-8" />
        </div>
      ) : spis.length === 0 ? (
        <Card className="bg-slate-900/60 border-slate-800">
          <CardContent className="py-12 text-center">
            <BarChart3 className="w-16 h-16 mx-auto mb-4 text-slate-700" />
            <h3 className="text-xl font-semibold text-white mb-2">No Safety Performance Indicators</h3>
            <p className="text-slate-400">
              {canCreate ? 'Create an SPI to start tracking safety metrics.' : 'No SPIs have been configured yet.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {spis.map(spi => (
            <SPICard
              key={spi.id}
              spi={spi}
              onAddData={openAddDataDialog}
              onViewDetails={openDetailDialog}
              language={language}
            />
          ))}
        </div>
      )}

      {/* Create SPI Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Target className="w-5 h-5 text-sky-400" />
              Create Safety Performance Indicator
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Name (English) *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  className="bg-slate-950 border-slate-800 mt-1"
                  placeholder="e.g., Incident Rate"
                  data-testid="spi-name-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">الاسم (العربية)</Label>
                <Input
                  value={formData.name_ar}
                  onChange={(e) => setFormData({...formData, name_ar: e.target.value})}
                  className="bg-slate-950 border-slate-800 mt-1"
                  dir="rtl"
                />
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Description *</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                required
                rows={2}
                className="bg-slate-950 border-slate-800 mt-1"
                data-testid="spi-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Category *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    {SPI_CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>
                        <div>
                          <span>{c.label}</span>
                          <span className="text-xs text-slate-500 ml-2">({c.description})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-300">Unit *</Label>
                <Select value={formData.unit} onValueChange={(v) => setFormData({...formData, unit: v})}>
                  <SelectTrigger className="bg-slate-950 border-slate-800 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-800">
                    <SelectItem value="%">Percentage (%)</SelectItem>
                    <SelectItem value="per 1000">Per 1000 operations</SelectItem>
                    <SelectItem value="per 10000">Per 10,000 hours</SelectItem>
                    <SelectItem value="count">Count</SelectItem>
                    <SelectItem value="days">Days</SelectItem>
                    <SelectItem value="hours">Hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-300">Target Value *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.target_value}
                  onChange={(e) => setFormData({...formData, target_value: parseFloat(e.target.value)})}
                  required
                  className="bg-slate-950 border-slate-800 mt-1"
                  data-testid="spi-target-input"
                />
              </div>
              <div>
                <Label className="text-slate-300">Alert Threshold *</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.alert_threshold}
                  onChange={(e) => setFormData({...formData, alert_threshold: parseFloat(e.target.value)})}
                  required
                  className="bg-slate-950 border-slate-800 mt-1"
                  data-testid="spi-threshold-input"
                />
                <p className="text-xs text-slate-500 mt-1">Alert when value exceeds this</p>
              </div>
            </div>

            <div>
              <Label className="text-slate-300">Calculation Method</Label>
              <Textarea
                value={formData.calculation_method}
                onChange={(e) => setFormData({...formData, calculation_method: e.target.value})}
                rows={2}
                className="bg-slate-950 border-slate-800 mt-1"
                placeholder="e.g., (incidents / flight hours) * 10000"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button type="submit" className="bg-sky-600 hover:bg-sky-700" data-testid="submit-spi-btn">Create SPI</Button>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)} className="border-slate-700 text-slate-300">Cancel</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Data Dialog */}
      <Dialog open={showAddDataDialog} onOpenChange={setShowAddDataDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-sky-400" />
              Add Data Point
            </DialogTitle>
          </DialogHeader>
          {selectedSPI && (
            <form onSubmit={handleAddData} className="space-y-4 py-4">
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <p className="text-sm text-slate-500">{selectedSPI.spi_number}</p>
                <p className="text-white font-medium">{selectedSPI.name}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-300">Value ({selectedSPI.unit}) *</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={dataForm.value}
                    onChange={(e) => setDataForm({...dataForm, value: parseFloat(e.target.value)})}
                    required
                    className="bg-slate-950 border-slate-800 mt-1"
                    data-testid="spi-value-input"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Period *</Label>
                  <Input
                    type="month"
                    value={dataForm.period}
                    onChange={(e) => setDataForm({...dataForm, period: e.target.value})}
                    required
                    className="bg-slate-950 border-slate-800 mt-1"
                    data-testid="spi-period-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-slate-300">Notes</Label>
                <Textarea
                  value={dataForm.notes}
                  onChange={(e) => setDataForm({...dataForm, notes: e.target.value})}
                  rows={2}
                  className="bg-slate-950 border-slate-800 mt-1"
                  placeholder="Optional notes about this data point..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="submit" className="bg-sky-600 hover:bg-sky-700" data-testid="submit-data-btn">Add Data</Button>
                <Button type="button" variant="outline" onClick={() => setShowAddDataDialog(false)} className="border-slate-700 text-slate-300">Cancel</Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail Dialog with Chart */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="bg-slate-900 border-slate-800 max-w-3xl">
          {selectedSPI && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm text-sky-400">{selectedSPI.spi_number}</span>
                  <Badge variant="outline" className="border-slate-700 text-slate-400">
                    {SPI_CATEGORIES.find(c => c.value === selectedSPI.category)?.label}
                  </Badge>
                  {(() => {
                    const trendInfo = TREND_INFO[selectedSPI.trend];
                    const TrendIcon = trendInfo.icon;
                    return (
                      <div className={cn("flex items-center gap-1 px-2 py-1 rounded-full text-xs", trendInfo.bg)}>
                        <TrendIcon className={cn("w-3 h-3", trendInfo.color)} />
                        <span className={trendInfo.color}>{trendInfo.label}</span>
                      </div>
                    );
                  })()}
                </div>
                <DialogTitle className="text-white text-xl">{selectedSPI.name}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <p className="text-slate-400">{selectedSPI.description}</p>

                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-slate-800/50 rounded-lg text-center">
                    <p className="text-sm text-slate-500">Current</p>
                    <p className="text-2xl font-bold text-white">{selectedSPI.current_value.toFixed(1)}</p>
                    <p className="text-xs text-slate-400">{selectedSPI.unit}</p>
                  </div>
                  <div className="p-3 bg-slate-800/50 rounded-lg text-center">
                    <p className="text-sm text-slate-500">Target</p>
                    <p className="text-2xl font-bold text-emerald-400">{selectedSPI.target_value}</p>
                    <p className="text-xs text-slate-400">{selectedSPI.unit}</p>
                  </div>
                  <div className="p-3 bg-slate-800/50 rounded-lg text-center">
                    <p className="text-sm text-slate-500">Alert Threshold</p>
                    <p className="text-2xl font-bold text-red-400">{selectedSPI.alert_threshold}</p>
                    <p className="text-xs text-slate-400">{selectedSPI.unit}</p>
                  </div>
                </div>

                {/* Trend Chart */}
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm text-slate-400">Trend (Last 12 Months)</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {spiHistory.length > 0 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={[...spiHistory].reverse()}>
                          <defs>
                            <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis dataKey="period" stroke="#64748b" fontSize={12} />
                          <YAxis stroke="#64748b" fontSize={12} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '4px' }}
                            labelStyle={{ color: '#f8fafc' }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="value" 
                            stroke="#0ea5e9" 
                            fillOpacity={1} 
                            fill="url(#colorValue)" 
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="h-[200px] flex items-center justify-center text-slate-500">
                        No historical data available
                      </div>
                    )}
                  </CardContent>
                </Card>

                {selectedSPI.calculation_method && (
                  <div className="p-3 bg-slate-800/50 rounded-lg">
                    <p className="text-sm text-slate-500">Calculation Method</p>
                    <p className="text-slate-300 font-mono text-sm mt-1">{selectedSPI.calculation_method}</p>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button 
                    onClick={() => { setShowDetailDialog(false); openAddDataDialog(selectedSPI); }}
                    className="bg-sky-600 hover:bg-sky-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add New Data Point
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
